import {AfterViewInit, Component, ElementRef, Inject, Input, OnChanges, OnDestroy, OnInit, PLATFORM_ID, SimpleChanges, ViewChild} from '@angular/core';
import {SwiperComponent} from 'swiper/angular';
import {UserViewModel} from '../../../features/user/settings/user.view-model';
import {SwiperOptions} from 'swiper/types';
import Swiper, {Controller, Keyboard, Mousewheel} from 'swiper';
import {SocialGridEntrySlide, SwiperItemInterface, SwiperItemsInterface} from './swiper-items.interface';
import {SocialListTypeEnum} from './social-list-type.enum';
import {SocialDataProviderService} from './social-data-provider.service';
import {isPlatformBrowser, KeyValue} from '@angular/common';
import {EventApiModel} from '../../../api/models/event.api.model';
import {UserApiModel} from '../../../api/models/user.api-model';

Swiper.use([Keyboard, Mousewheel, Controller]);

@Component({
  selector: 'app-social-grid-list',
  templateUrl: './social-grid-list.component.html',
  styleUrls: ['./social-grid-list.component.scss'],
  providers: [
    SocialDataProviderService
  ]
})
export class SocialGridListComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() public listType: SocialListTypeEnum;
  @Input() public user: UserViewModel;
  @Input() public event: EventApiModel;
  @Input() public projectId?: number;
  @Input() public halfWidthTabs = false;
  @Input() public fillAvailableHeight = false;
  @Input() public handOffEdgeSwipes = false;
  @Input() public keyboardEnabled = true;
  @Input() public entrySlide?: SocialGridEntrySlide | null;
  public subjectType: string;
  public config: SwiperOptions;
  public childConfig: SwiperOptions;
  @ViewChild('mainSwiper') public mainSwiper: SwiperComponent;
  @ViewChild('slaveSwiper') public slaveSwiper: SwiperComponent;
  @ViewChild('socialNav', {read: ElementRef}) private socialNav?: ElementRef<HTMLElement>;
  public currentItem: SwiperItemInterface | null = null;
  private swiperIndex = 0;
  private navResizeObserver?: ResizeObserver;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}


  @Input() public slides: SwiperItemsInterface = {};

  ngOnInit(): void {
    this.config = {
      slidesPerView: this.halfWidthTabs ? 2 : 3,
      autoHeight: true,
      keyboard: {
        enabled: this.keyboardEnabled,
        onlyInViewport: false,
      },
    };
    this.childConfig = {
      slidesPerView: 1,
      autoHeight: true,
      initialSlide: this.getEntrySlideIndex(),
      nested: this.handOffEdgeSwipes,
      touchReleaseOnEdges: this.handOffEdgeSwipes,
      edgeSwipeDetection: this.handOffEdgeSwipes,
      edgeSwipeThreshold: 24,
      touchStartPreventDefault: true,
      keyboard: {
        enabled: this.keyboardEnabled,
        onlyInViewport: false,
      },
    };
    this.syncCurrentItem();
  }

  ngAfterViewInit(): void {
    if (!this.fillAvailableHeight || !isPlatformBrowser(this.platformId) || !this.socialNav) {
      return;
    }
    this.updateNavHeight();
    if (typeof ResizeObserver !== 'undefined') {
      this.navResizeObserver = new ResizeObserver(() => this.updateNavHeight());
      this.navResizeObserver.observe(this.socialNav.nativeElement);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keyboardEnabled']) {
      this.syncKeyboard(this.mainSwiper?.swiperRef);
      this.syncKeyboard(this.slaveSwiper?.swiperRef);
    }
    if (changes['slides']) {
      this.syncCurrentItem();
    }
    if (this.childConfig && (changes['slides'] || changes['entrySlide'])) {
      this.childConfig.initialSlide = this.getEntrySlideIndex();
    }
    if ((changes['slides'] || changes['entrySlide']) && this.entrySlide) {
      this.applyEntrySlide();
    }
  }

  public onMainSwiperReady(swiper: Swiper): void {
    this.syncKeyboard(swiper);
    if (this.handOffEdgeSwipes && this.entrySlide) {
      this.updateNavigationIndex(this.getEntrySlideIndex(), swiper);
    }
  }

  public onChildSwiperReady(swiper: Swiper): void {
    this.syncKeyboard(swiper);
    this.applyEntrySlide(swiper);
  }

  private syncKeyboard(swiper?: Swiper): void {
    if (!swiper || swiper.destroyed || !isPlatformBrowser(this.platformId)) {
      return;
    }
    if (this.keyboardEnabled) {
      swiper.keyboard?.enable();
    } else {
      swiper.keyboard?.disable();
    }
  }

  public controlChildSwiper(): void {
    if (!this.slaveSwiper?.swiperRef || !this.config) {
      return;
    }

    this.config.controller = {
      control: this.slaveSwiper.swiperRef
    };
    this.mainSwiper.config = this.config;
  }

  public hasSlides(): boolean {
    return Object.keys(this.slides || {}).length > 0;
  }

  public isCollectionContextReady(): boolean {
    switch (this.listType) {
      case SocialListTypeEnum.FOLLOWS:
      case SocialListTypeEnum.REACTIONS:
      case SocialListTypeEnum.EVENTS:
        return !!this.user?.toApiModel;
      case SocialListTypeEnum.EVENT_PARTICIPANTS:
        return !!this.event;
      case SocialListTypeEnum.PROJECT_EVENTS:
        return typeof this.projectId === 'number';
      default:
        return false;
    }
  }

  public getAuthorApiModel(): UserApiModel | null {
    return this.user?.toApiModel?.() || null;
  }

  setActiveSlide(swiper: Swiper[]): void {
    const mainSwiperRef = this.mainSwiper?.swiperRef;
    const activeIndex = swiper?.[0]?.activeIndex;
    if (!mainSwiperRef || typeof activeIndex !== 'number') {
      return;
    }

    mainSwiperRef.activeIndex = activeIndex;
    mainSwiperRef.updateSlidesClasses();
  }

  setActiveSlaveSlide(index: number, item: SwiperItemInterface, swiper: SwiperComponent, event: Event): void {
    if (!item) {
      return;
    }

    this.currentItem = item;
    this.swiperIndex = index;
    this.updateSlaveSwiper(index);
    item.command?.(index, swiper, event);
  }

  updateSlaveSwiper(index: number): void {
    const slaveSwiperRef = this.slaveSwiper?.swiperRef;
    const mainSwiperRef = this.mainSwiper?.swiperRef;

    if (!slaveSwiperRef || !mainSwiperRef) {
      return;
    }

    slaveSwiperRef.slideTo(index);
    mainSwiperRef.activeIndex = index;
    mainSwiperRef.updateSlidesClasses();
    slaveSwiperRef.updateSlidesClasses();
  }

  originalOrder(a: KeyValue<string, SwiperItemInterface>, b: KeyValue<string, SwiperItemInterface>): number {
    return (a.value.order || 0) > (b.value.order || 0) ? 1 : (b.value.order > a.value.order ? -1 : 0) ;
  }

  ngOnDestroy(): void {
    this.navResizeObserver?.disconnect();
    this.destroySwiper(this.mainSwiper);
    this.destroySwiper(this.slaveSwiper);
  }

  private updateNavHeight(): void {
    const height = this.socialNav?.nativeElement.getBoundingClientRect().height;
    if (height) {
      this.host.nativeElement.style.setProperty('--social-grid-nav-height', `${Math.ceil(height)}px`);
    }
  }

  private syncCurrentItem(): void {
    const items = this.getOrderedSlides();
    if (!items.length) {
      this.currentItem = null;
      this.swiperIndex = 0;
      return;
    }

    this.swiperIndex = this.handOffEdgeSwipes ? this.getEntrySlideIndex() : 0;
    this.currentItem = items[this.swiperIndex] || null;
  }

  private getOrderedSlides(): SwiperItemInterface[] {
    return Object.values(this.slides || {}).sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  private getEntrySlideIndex(): number {
    return this.handOffEdgeSwipes && this.entrySlide?.edge === 'last'
      ? Math.max(0, this.getOrderedSlides().length - 1)
      : 0;
  }

  private applyEntrySlide(swiper = this.slaveSwiper?.swiperRef): void {
    if (!this.handOffEdgeSwipes || !this.entrySlide || !isPlatformBrowser(this.platformId) || !swiper || swiper.destroyed) {
      return;
    }
    const index = this.getEntrySlideIndex();
    swiper.slideTo(index, 0, false);
    this.swiperIndex = index;
    this.currentItem = this.getOrderedSlides()[index] || null;
    this.updateNavigationIndex(index);
  }

  private updateNavigationIndex(index: number, mainSwiperRef = this.mainSwiper?.swiperRef): void {
    if (!mainSwiperRef || mainSwiperRef.destroyed) {
      return;
    }
    mainSwiperRef.activeIndex = index;
    mainSwiperRef.updateSlidesClasses();
  }

  private destroySwiper(swiperComponent?: SwiperComponent): void {
    const swiperRef = swiperComponent?.swiperRef;
    if (!swiperRef || swiperRef.destroyed) {
      return;
    }

    swiperRef.destroy(true, true);
  }

}
